import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AuthService} from '@app/services/auth/auth.service';
import {Project} from '@models/project.model';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {AppState, selectAuthState} from '@store/app.states';
import {EMPTY, of} from 'rxjs';
import {catchError, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';

import {
  SwitchDebugMode as SwitchDebugModeForActivePublication
} from '@app/components/publications/create-publication/_store/active-publication.actions';
import {
  SwitchDebug as SwitchDebugModeForAnnotation
} from '@app/components/projects/surveys/annotation/store/annotation.actions';
import {
  SwitchDebug as SwitchDebugModeForPublicationList
} from '@app/components/publications/store/publications/publications.actions';
import {SwitchDebugMode as SwitchDebugModeFor4Dm1n} from '@app/4dm1n/users-list/store/users-list.actions';
import {
  AuthActionTypes,
  ChangeCurrentProjectId,
  ChangeLang, ChangeLangSuccess,
  LogIn,
  LogInFailure,
  LogInSuccess, RefreshToken, RefreshTokenSuccess,
  SecretKeyLogin,
  TokenLogin,
  UpdateProfilFailure,
} from '../actions/auth.actions';
import {RefreshProjectsList, RefreshSurveysList} from '../actions/projects.actions';
import {FUSE_CONFIG, FuseConfigService} from '../../../@fuse/services/config.service';
import {retrieveFirebaseJWTTokenSuccess} from '@app/firebase-status/firebase-status.actions';
import {WhoisOnlineService} from '@app/services/IdleAndWhoseOnline/whois-online.service';
import {FirebaseStatusService} from '@app/firebase-status/firebase-status.service';
import {locale as devXtremeLocalization} from 'devextreme/localization';
import {AppUserContextService} from '../../../@shared/_referencesLists/reference-lists/app-user-context.service';

// const _ = require('lodash');
// import * as _ from 'lodash';
import { find } from 'lodash';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    private store: Store<AppState>,
    // tslint:disable-next-line:variable-name
    @Inject(FUSE_CONFIG) private _default_config,
    // tslint:disable-next-line:variable-name
    private _fuseConfigService: FuseConfigService,
    private translate: TranslateService,
    private whoisOnline: WhoisOnlineService,
    // public angularFireAuth: AngularFireAuth,
    public firebaseStatus: FirebaseStatusService,
    //
    //

    private appUserContextService: AppUserContextService, // <--- Nécessaire pour la synchro avec Firebase :)

  ) {
  }

  LogIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGIN),
      map((action: LogIn) => action.payload),
      switchMap((payload) => {

        //
        // [1] - Login action!
        //
        return this.authService.logIn(payload.email, payload.password).pipe(
          map((response) => {
            if (!response.data.userContext.curLang) {
              response.data.userContext.curLang = localStorage.getItem('locale');
            }

            return new LogInSuccess({
              token: response.data.token,
              email: response.data.email,
              context: response.data.userContext,
            });

          }),

          catchError((error) => of(new LogInFailure({error}))),
        );
      }),
    ),
  );

  TokenLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.TOKEN_LOGIN),
      map((action: TokenLogin) => action.payload),
      switchMap((payload) => {
        return this.authService.tokenLogin(payload.token).pipe(
          map((response) => {
            return new LogInSuccess({
              token: response.data.token,
              email: response.data.email,
              context: response.data.userContext,
              gotoRoute: payload.gotoRoute,
            });
          }),
          catchError((error) => of(new LogInFailure({error}))),
        );
      }),
    ),
  );

  SecretKeyLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.SECRET_KEY_LOGIN),
      map((action: SecretKeyLogin) => action.payload),
      switchMap((payload) => {
        return this.authService.secretKeyLogin(payload.secretKey).pipe(
          map((response) => {
            return new LogInSuccess({
              token: response.data.token,
              email: response.data.email,
              context: response.data.userContext,
              gotoRoute: payload.gotoRoute,
            });
          }),
          catchError((error) => of(new LogInFailure({error}))),
        );
      }),
    ),
  );

  LogInSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          AuthActionTypes.LOGIN_SUCCESS,
          AuthActionTypes.LOGIN_ALREADY,
        ),
        map((action) => action),
        tap((user: any) => {

          this.authService.authStatus$.next(
            {
              ...this.authService.authStatus$.value,
              isLogged: true,
            }
          );
          localStorage.setItem('token', user.payload.token);
          localStorage.setItem('email', user.payload.email);
          localStorage.setItem('context', JSON.stringify(user.payload.context));

          // Réinitialiser la navbar à visible (elle était cachée par le login component)
          this._fuseConfigService.setConfig({
            layout: {
              navbar: {
                hidden: false,
              },
            },
          });

          if (this.authService.hasContext()) {
            this.authService.switchLang(this.authService.getContext().curLang);
          } else {

          }
          this.whoisOnline.updateIdentity(
            `${this.authService.getContext().id}${this.authService.getContext().userAccess.includes('GOD_MODE') ? '*' : ''}`,
            this.authService.getContext().displayName,
            this.authService.getContext().organizationId,
            this.authService.getContext().userAccess.includes('GOD_MODE'),
          );
          // On récupère un token Firebase ! --> Dans le refresh du token maintenant :)
          this.store.dispatch(new RefreshProjectsList({forceRefresh: true}));
          this.store.dispatch(new RefreshToken({jwtToken: this.authService.getToken()}));
          if (user.type !== '[Auth] Login Already') {
            if (user.payload.gotoRoute) {
              this.router.navigateByUrl(user.payload.gotoRoute).then();
            } else {
              this.router.navigateByUrl('/').then();
            }
          }

        }),
      ),
    {dispatch: false},
  );

  RefreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.REFRESH_TOKEN),
      map((action: RefreshToken) => action.payload),
      switchMap((payload) => {

        return this.authService.refreshToken(payload.jwtToken).pipe(
          map((response) => {
            return new RefreshTokenSuccess({response: response.data});

          }),
          catchError((error) => of(new LogInFailure({error}))),
        );
      }),
    ),
  );

  RefreshTokenSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActionTypes.REFRESH_TOKEN_SUCCESS),
        map((action: RefreshTokenSuccess) => action.payload),
        tap((payload) => {

          this.authService.authStatus$.next(
            {
              ...this.authService.authStatus$.value,
              isLogged: true,
            }
          );

          // console.log('=> tokens refreshed:', payload.response.datas.result);
          const {jwtToken, firebaseJWT} = payload.response.datas.result;
          localStorage.setItem('token', jwtToken);
          this.store.dispatch(retrieveFirebaseJWTTokenSuccess({token: firebaseJWT}));

        }),
      ),
    {dispatch: false}
  );


  LogInFailure$ = createEffect(() => this.actions$.pipe(ofType(AuthActionTypes.LOGIN_FAILURE)), {
    dispatch: false,
  });

  //
  // Logout : on vide aussi le authService ?
  //
  LogOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.LOGOUT),
        tap((action) => {

          console.log('*** Logout :(');

          this.authService.authStatus$.next(
            {
              ...this.authService.authStatus$.value,
              isLogged: false,
            }
          );

          localStorage.removeItem('token');
          localStorage.removeItem('email');
          localStorage.removeItem('context');

          this.whoisOnline.endSession();

          // Le signout pour firebase ne marche pas bien....
          // this.firebaseStatus.doSignOut();

          // on va rerouter toute l'app pour pouvoir relancer une session
          // this.router.navigateByUrl('/auth/login');
          window.location.replace('/auth/login');
          // window.location.href = '/auth/login';

        }),
      ),
    {dispatch: false},
  );
  //
  //
  //

  GetStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.GET_STATUS),
        switchMap((_) => this.authService.getStatus()),
        tap(),
      ),
    {dispatch: false},
  );

  ChangeCurrentProjectId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.CHANGE_CURRENT_PROJECT_ID),
        map((action: ChangeCurrentProjectId) => action.payload),
        map((payload) => {
          this.store
            .select('projects')
            .pipe(take(1))
            .subscribe((data) => {
              // console.log('****** Change current Project ID !');

              const nProject: Project = find(data.projectsList, {
                id: payload.project.id,
              });

              // console.log(nProject.language);
              // console.log(nProject.langAvail);

              let langAvail = nProject.langAvail;
              if (!langAvail) {
                langAvail = ['fr', 'en'];
              }

              this.translate.addLangs(langAvail);
              this.translate.setDefaultLang(nProject.language);
              this.translate.use(nProject.language);

              //
              // console.log('Project changed : refresh SURVEY list!');
              this.store.dispatch(
                new RefreshSurveysList({
                  projectId: nProject.id,
                  forceRefresh: true,
                }),
              );
            });
        }),
      ),
    {dispatch: false},
  );

  UpdateCurrentLang$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.CHANGE_LANG),
        map((action: ChangeLang) => action.payload),
        withLatestFrom(this.store.select(selectAuthState)),
        switchMap(([payload, data]) => {

          const lastLang = this.authService.getContext().curLang;
          const newLang = payload.newLang;

          // console.log(`------------------- UPDATECURRENTLANG: ${this.authService.getContext().curLang} ---> ${payload.newLang} (${this.authService.getContext().id}) -------------------------------------------`);

          if (payload.newLang.length === 0) {
            return EMPTY;
          }

          this.authService.updateLocalStorageContextLang(payload.newLang);
          // Set the selected language for the toolbar
          devXtremeLocalization(payload.newLang);
          // Use the selected language for translations
          this.translate.use(payload.newLang);
          localStorage.setItem('locale', payload.newLang);
          this.store.dispatch(new ChangeLangSuccess({newLang: payload.newLang}));

          // console.log('this.authService.isLogged()', this.authService.isLogged());
          // console.log('data.user?.context', data.user?.context);
          // console.log('payload.newLang !== this.authService.getContext().curLang', payload.newLang !== this.authService.getContext().curLang);

          if (
            this.authService.isLogged()
            && data.user?.context
            && lastLang !== newLang
            // && payload.newLang !== this.authService.getContext().curLang
          ) {

            console.log('Change Lang in database :', payload.newLang);

            return this.authService.updateProfil(
              data.user.context.firstname,
              data.user.context.lastname,
              data.user.context.email,
              payload.newLang
            ).pipe(catchError((error) => of(new UpdateProfilFailure())));

          } else {
            return EMPTY;
          }
        }),
      ),
    {dispatch: false},
  );

  propagateDebugMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.SWITCH_DEBUG_MODE),
        withLatestFrom(this.store.select(selectAuthState)),
        tap((data) => {
          this.store.dispatch(new SwitchDebugModeForActivePublication());
          this.store.dispatch(new SwitchDebugModeForAnnotation());
          this.store.dispatch(new SwitchDebugModeForPublicationList());
          this.store.dispatch(SwitchDebugModeFor4Dm1n());
          return EMPTY;
        }),
      ),
    {dispatch: false},
  );
}

export function clearState(reducer) {
  return (state, action) => {
    if (action.type === AuthActionTypes.LOGOUT) {
      // console.log('>>>> Clear state !');
      state = undefined;
    }
    return reducer(state, action);
  };
}
